import React, { Component } from 'react';
import { DateTime } from 'luxon';
import PropTypes from 'prop-types';

import FacilityHelper from '../helpers/FacilityHelper';
import CurrencyHelper from '../helpers/CurrencyHelper';
import StyleHelper from '../../shared/helpers/StyleHelper';

import './FacilityTiles.css';
import Tooltip from './Tooltip';
import { isScrolledToBottom } from '../../shared/utils/scroll';

class FacilityTiles extends Component {
  constructor(props) {
    super(props);
    this.state = {
      show: [],
      width: null,
      currentHour: DateTime.local().hour,
      popoverId: null,
    };
    this.resizeFunction = null;
    this.resizeTimer = null;
    this.rowHeight = 100;
    this.areaScroll = false;
    this.labelHeight = this.rowHeight + 2;
  }

  toggleMore(identifier) {
    const show = this.state.show.slice();
    const index = show.indexOf(identifier);
    if (index < 0) {
      show.push(identifier);
    } else {
      show.splice(index, 1);
    }
    this.setState({
      show,
    });
  }

  handleResize() {
    const node = this.node;
    if (node && typeof node.getBoundingClientRect === 'function') {
      this.setState({
        width: node.getBoundingClientRect().width,
      });
    }
  }

  componentDidMount() {
    this.resizeFunction = () => {
      clearTimeout(this.resizeTimer);
      this.resizeTimer = setTimeout(() => {
        this.handleResize();
      }, 250);
    };
    window.addEventListener('resize', this.resizeFunction);
    this.handleResize();
    this.scrollFunction = () => {
      clearTimeout(this.scrollTimer);
      this.scrollTimer = setTimeout(() => {
        this.handleScroll();
      }, 100);
    };
    window.addEventListener('scroll', this.scrollFunction);
    this.attachAreaScroll();
    this.firstScrollHandle = setTimeout(() => {
      this.handleScroll();
    }, 500);
  }

  componentWillUnmount() {
    clearTimeout(this.resizeTimer);
    clearTimeout(this.firstScrollHandle);
    window.removeEventListener('resize', this.resizeFunction);
    clearTimeout(this.scrollTimer);
    window.removeEventListener('scroll', this.scrollFunction);
    const node = this.area;
    if (node) {
      node.removeEventListener('scroll', this.scrollFunction);
    }
  }

  attachAreaScroll() {
    if (!this.areaScroll) {
      const node = this.area;
      if (node) {
        node.addEventListener('scroll', this.scrollFunction);
        this.areaScroll = true;
      }
    }
  }

  handleScroll() {
    // do nothing
    const isAtBottom = isScrolledToBottom(this.area);
    if (!isAtBottom && !this.state.tooltip) {
      this.setState({ tooltip: true });
    } else if (isAtBottom && this.state.tooltip) {
      this.setState({ tooltip: false });
    }
  }

  handleClick(facilityUse) {
    this.props.updateSelectedFacility(facilityUse);
  }

  componentDidUpdate() {
    if (typeof this.props.didUpdate === 'function') {
      this.props.didUpdate();
    }
    this.attachAreaScroll();
    this.handleScroll();
  }

  render() {
    if (this.state.width !== null) {
      const facilities = FacilityHelper.sortByActivity(this.props.facilities['imin:item'] || []);
      const timetableContainer = [];
      const facilityTiles = [];
      const { tilesPerRow } = this.props;

      let width = `calc(${100 / tilesPerRow}% - 2px)`;

      const branded = StyleHelper.applyCustomStyle(
        this.props.customStyle,
        '.FacilityTiles .facility-tile-row .facility-tile.branded'
      )
        ? 'branded'
        : '';

      const ttStyle = {};
      if (this.props.height) {
        /* We make room for the:
        - Filters
        - Space at the bottom for the "scroll down for more" tooltip */
        ttStyle.maxHeight = `${this.props.height - 9.5}rem`;
      }

      let row = [];

      for (let index = 0; index < facilities.length; index++) {
        let price;
        if (
          !FacilityHelper.isWithinFilterParameters(
            this.props.isCategoryFilter,
            facilities[index],
            this.props.filter
          )
        ) {
          continue;
        }
        if (facilities[index]['imin:aggregateOffer']) {
          const { publicAdult } = facilities[index]['imin:aggregateOffer'];
          if (publicAdult) {
            price = CurrencyHelper.format('en-GB', publicAdult.priceCurrency, publicAdult.price);
          }
        }
        row.push(
          <div
            className={`facility-tile ${branded}`}
            onClick={() => this.handleClick(facilities[index])}
            key={`facilityTileRow-${facilityTiles.length}-facilityTile-${row.length}`}
            style={{ width }}
          >
            <div>{facilities[index].name}</div>
            <div>{FacilityHelper.getActivity(facilities[index])}</div>
            {this.props.showPrices && <div>{price}</div>}
          </div>
        );

        if (row.length === tilesPerRow) {
          facilityTiles.push(
            <div className="facility-tile-row" key={`facilityTileRow-${facilityTiles.length}`}>
              {row}
            </div>
          );
          row = [];
        }
      }

      if (row.length > 0) {
        facilityTiles.push(
          <div className="facility-tile-row" key={`facilityTileRow-${facilityTiles.length}`}>
            {row}
          </div>
        );
      }

      return (
        <div ref={(node) => (this.node = node)}>
          <div className="FacilityTiles">
            {timetableContainer}
            <div ref={(area) => (this.area = area)} className="tiles-area" style={ttStyle}>
              {facilityTiles}
            </div>
          </div>
          {this.state.tooltip ? <Tooltip title="Scroll down for more slots" /> : null}
        </div>
      );
    }
    this.areaScroll = false;
    return <div ref={(node) => (this.node = node)} className="FacilityTiles"></div>;
  }
}

FacilityTiles.propTypes = {
  didUpdate: PropTypes.func,
  date: PropTypes.string,
  facilities: PropTypes.shape({
    'imin:item': PropTypes.array,
  }),
  filter: PropTypes.object,
  customStyle: PropTypes.object,
  hourStyle: PropTypes.object,
  checkoutUrl: PropTypes.string,
  checkoutTheme: PropTypes.string,
  buttonStyle: PropTypes.object,
  height: PropTypes.number,
  tilesPerRow: PropTypes.number,
  updateSelectedFacility: PropTypes.func,
  isCategoryFilter: PropTypes.bool,
  showPrices: PropTypes.bool,
};

export default FacilityTiles;
